import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Tooltip,
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useAuthStore } from '../contexts/authStore';
import { useMeetingStore } from '../contexts/meetingStore';
import { useVideoEffectsStore, connectAuthStore } from '../contexts/videoEffectsStore';
import { useVideoEffects } from '../hooks/useVideoEffects';
import VideoEffectsPanel from '../components/meeting/VideoEffectsPanel';
import * as api from '../services/api';

export default function JoinPage() {
  const { meetingCode } = useParams();
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const { user, token, isAuthenticated } = authStore;
  const { setMeeting, setParticipant, setAudioEnabled, setVideoEnabled, audioEnabled, videoEnabled } = useMeetingStore();

  // Video effects
  const {
    effectType,
    effectsPanelOpen,
    setEffectsPanelOpen,
    loadFromServer,
    fetchFromServer,
    isSupported: effectsSupported,
  } = useVideoEffectsStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meetingInfo, setMeetingInfo] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestGender, setGuestGender] = useState('male');
  const [joining, setJoining] = useState(false);
  const [mediaError, setMediaError] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const hiddenVideoRef = useRef(null);

  // Video effects processing
  const { getOutputStream } = useVideoEffects(
    streamRef.current,
    effectType !== 'none' && videoEnabled
  );

  // Connect auth store for video effects syncing
  useEffect(() => {
    connectAuthStore(authStore);
  }, [authStore]);

  // Load user video preferences on mount (authenticated users)
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchFromServer(token);
    }
  }, [isAuthenticated, token, fetchFromServer]);

  useEffect(() => {
    loadMeetingInfo();
    setupPreview();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [meetingCode]);

  // Reconnect video when re-enabled (element gets remounted)
  // Use processed stream if effects are active
  useEffect(() => {
    if (videoEnabled && videoRef.current && streamRef.current) {
      const outputStream = getOutputStream();
      videoRef.current.srcObject = outputStream || streamRef.current;
    }
  }, [videoEnabled, effectType, getOutputStream]);

  const loadMeetingInfo = async () => {
    try {
      setLoading(true);
      const response = await api.getMeeting(meetingCode, token);
      setMeetingInfo(response.meeting);

      if (response.meeting.status === 'ended') {
        setError('This meeting has ended');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const setupPreview = async () => {
    // Check if mediaDevices is available (requires secure context: HTTPS or localhost)
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
      if (!isSecure) {
        setMediaError('Camera/microphone access requires HTTPS or localhost. Please access via localhost:3003');
      } else {
        setMediaError('Your browser does not support camera/microphone access');
      }
      setVideoEnabled(false);
      setAudioEnabled(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: true,
      });
      streamRef.current = stream;

      // Set up hidden video element for effects processing
      if (hiddenVideoRef.current) {
        hiddenVideoRef.current.srcObject = stream;
        hiddenVideoRef.current.play().catch(() => {});
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setMediaError(null);
    } catch (err) {
      console.error('Failed to get media devices:', err);
      if (err.name === 'NotAllowedError') {
        setMediaError('Camera/microphone permission denied. Please allow access and refresh.');
      } else if (err.name === 'NotFoundError') {
        setMediaError('No camera or microphone found on this device.');
      } else {
        setMediaError(`Media error: ${err.message}`);
      }
      setVideoEnabled(false);
      setAudioEnabled(false);
    }
  };

  const toggleVideo = () => {
    if (streamRef.current) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
      }
    }
  };

  const toggleAudio = () => {
    if (streamRef.current) {
      const audioTrack = streamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
      }
    }
  };

  const handleJoin = async () => {
    if (!isAuthenticated && !guestName.trim()) {
      setError('Please enter your name');
      return;
    }

    setJoining(true);

    // Store meeting and participant info
    setMeeting(meetingInfo);
    setParticipant({
      displayName: isAuthenticated ? user.displayName : guestName,
      gender: isAuthenticated ? user.gender : guestGender,
      userId: isAuthenticated ? user.id : null,
    });

    // Stop preview stream (will be recreated in meeting page)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }

    // Save state to sessionStorage for reliability across navigation
    const mediaState = useMeetingStore.getState();
    console.log('[JoinPage] Navigating with audioEnabled:', mediaState.audioEnabled, 'videoEnabled:', mediaState.videoEnabled);
    sessionStorage.setItem('meetingMediaState', JSON.stringify({
      audioEnabled: mediaState.audioEnabled,
      videoEnabled: mediaState.videoEnabled,
    }));

    // Navigate to meeting
    navigate(`/meeting/${meetingCode}`, {
      state: {
        guestName: !isAuthenticated ? guestName : null,
        guestGender: !isAuthenticated ? guestGender : null,
      },
    });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !meetingInfo) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <Card sx={{ maxWidth: 400, textAlign: 'center', p: 4 }}>
          <VideoCallIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 2 }}>
            Meeting Not Found
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            Go Home
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          {/* Video preview */}
          <Grid item xs={12} md={7}>
            <Card sx={{ overflow: 'hidden' }}>
              <Box
                sx={{
                  position: 'relative',
                  aspectRatio: '16/9',
                  bgcolor: 'black',
                }}
              >
                {/* Video preview - only mounted when enabled */}
                {videoEnabled && (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transform: 'scaleX(-1)',
                    }}
                  />
                )}
                {!videoEnabled && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'surface.main',
                    }}
                  >
                    <Typography variant="h2" color="text.secondary">
                      {(isAuthenticated ? user?.displayName : guestName || 'Guest')?.charAt(0).toUpperCase()}
                    </Typography>
                  </Box>
                )}

                {/* Controls */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color={audioEnabled ? 'primary' : 'error'}
                    onClick={toggleAudio}
                    sx={{ borderRadius: '50%', minWidth: 56, height: 56 }}
                  >
                    {audioEnabled ? <MicIcon /> : <MicOffIcon />}
                  </Button>
                  <Button
                    variant="contained"
                    color={videoEnabled ? 'primary' : 'error'}
                    onClick={toggleVideo}
                    sx={{ borderRadius: '50%', minWidth: 56, height: 56 }}
                  >
                    {videoEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
                  </Button>
                  {effectsSupported && (
                    <Tooltip title="Effetti Video">
                      <Button
                        variant="contained"
                        color={effectType !== 'none' ? 'secondary' : 'primary'}
                        onClick={() => setEffectsPanelOpen(true)}
                        sx={{ borderRadius: '50%', minWidth: 56, height: 56 }}
                      >
                        <AutoFixHighIcon />
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Join form */}
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  Ready to join?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {meetingInfo?.title || 'Meeting'}
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {mediaError && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {mediaError}
                  </Alert>
                )}

                {!isAuthenticated && (
                  <>
                    <TextField
                      fullWidth
                      label="Your Name"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      sx={{ mb: 2 }}
                      required
                    />
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Voice Gender (for translation)</InputLabel>
                      <Select
                        value={guestGender}
                        onChange={(e) => setGuestGender(e.target.value)}
                        label="Voice Gender (for translation)"
                      >
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                      </Select>
                    </FormControl>
                  </>
                )}

                {isAuthenticated && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'surface.dark', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Joining as
                    </Typography>
                    <Typography variant="h6">{user?.displayName}</Typography>
                  </Box>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleJoin}
                  disabled={joining || (!isAuthenticated && !guestName.trim())}
                >
                  {joining ? 'Joining...' : 'Join Meeting'}
                </Button>

                {!isAuthenticated && (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                    <Button color="primary" onClick={() => navigate('/login')}>
                      Sign in
                    </Button>
                    to create your own meetings
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Hidden video element for effects processing */}
      <video
        ref={hiddenVideoRef}
        autoPlay
        playsInline
        muted
        style={{ display: 'none' }}
      />

      {/* Video Effects Panel */}
      <VideoEffectsPanel
        open={effectsPanelOpen}
        onClose={() => setEffectsPanelOpen(false)}
      />
    </Box>
  );
}
